import React, { useCallback, useEffect, useState } from "react";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import toast from "react-hot-toast";

// ⚠️ Security note: CLIENT_SECRET should not be used on the frontend.
//    Move the PayPal token exchange to your backend to keep credentials private.
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_PAYPAL_SECRET_KEY;
const PARTNER_ID = process.env.REACT_APP_PAYPAL_PARTNER_ID || "5ZPAUD3E2QNVY";
const PAYPAL_API = "https://api-m.paypal.com";
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://chefrepublicwebapi-dev.portalwiz.in/api";

/**
 * Strips all HTML tags from a string and removes any bare URLs left behind by <a> tags.
 * Used to sanitize API error messages that contain raw HTML.
 */
const stripHtml = (html) =>
  html
    ? html
        .replace(/<a\b[^>]*>.*?<\/a>/gi, "")
        .replace(/<[^>]*>/g, "")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const getPayPalAccessToken = async () => {
  const basicAuth = btoa(`${CLIENT_ID}:${CLIENT_SECRET}`);
  const res = await fetch(`${PAYPAL_API}/v1/oauth2/token`, {
    method: "POST",
    headers: {
      Authorization: `Basic ${basicAuth}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: "grant_type=client_credentials",
  });
  const data = await res.json();
  return data.access_token;
};

export default function LinkPayment({
  isPaymentLinked,
  className,
  style,
  onAddChefAccountSuccess,
}) {
  const userId = localStorage.getItem("logged_user_id");

  const [loading, setLoading] = useState(false);
  const [showPaymentsReceivableError, setShowPaymentsReceivableError] = useState(false);
  const [paymentsReceivableErrorMsg, setPaymentsReceivableErrorMsg] = useState("");
  const [showEmailConfirmationError, setShowEmailConfirmationError] = useState(false);
  const [emailConfirmationErrorMsg, setEmailConfirmationErrorMsg] = useState("");

  const checkAccountStatus = useCallback(
    async (merchantId) => {
      try {
        const accessToken = await getPayPalAccessToken();
        const res = await fetch(
          `${PAYPAL_API}/v1/customer/partners/${PARTNER_ID}/merchant-integrations/${merchantId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const statusData = await res.json();

        if (!statusData.primary_email_confirmed) {
          const syncRes = await fetch(`${process.env.REACT_APP_BASE_URL}user/syncChefPayPalStatus`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId }),
          });
          const syncData = await syncRes.json();

          if (syncData.status === false) {
            setEmailConfirmationErrorMsg(
              syncData.message || "Please confirm your PayPal email address to continue."
            );
            setShowEmailConfirmationError(true);
            return;
          }
        }

        if (!statusData.payments_receivable) {
          setShowPaymentsReceivableError(true);
        } else if (!statusData.primary_email_confirmed) {
          setShowEmailConfirmationError(true);
        } else {
          toast.success("Merchant account linked successfully");
        }
      } catch (err) {
        console.error("Account status check failed:", err);
      }
    },
    [userId]
  );

  const saveMerchantIdToBackend = useCallback(
    async (merchantId, email) => {
      try {
        // First API call: add_chef_account
        const res = await fetch(`${API_BASE_URL}/user/add_chef_account`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            merchantId,
            Email: email,
            merchantIdInPayPal: merchantId,
            permissionsGranted: true,
            accountStatus: "",
            consentStatus: "",
            isEmailConfirmed: "",
            returnMessage: "",
          }),
        });
        const data = await res.json();

        if (data.status === true) {
          onAddChefAccountSuccess?.();
          
          // Second API call: get_chef_account_details after add_chef_account succeeds
          try {
            const accountRes = await fetch(`${process.env.REACT_APP_BASE_URL}user/get_chef_account_details`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ user_id: userId }),
            });
            const accountData = await accountRes.json();
            
            // Log the account details for debugging
            console.log("Chef account details:", accountData);
            
          } catch (accountErr) {
            console.error("Get chef account details error:", accountErr);
          }
          
          // Finally check account status
          await checkAccountStatus(merchantId);
        } else if (data.status === false) {
          setPaymentsReceivableErrorMsg(
            data.message || "Something went wrong. Please try again."
          );
          setShowPaymentsReceivableError(true);
        }
      } catch (err) {
        console.error("Save merchant error:", err);
      } finally {
        setLoading(false);
      }
    },
    [userId, onAddChefAccountSuccess, checkAccountStatus]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const merchantId = params.get("merchantIdInPayPal");
    const email = params.get("email");

    if (merchantId) {
      setLoading(true);
      saveMerchantIdToBackend(merchantId, email);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [saveMerchantIdToBackend]);

  const handleOnboarding = async () => {
    setLoading(true);
    try {
      const accessToken = await getPayPalAccessToken();
      const currentPath = window.location.pathname.replace(/^\//, "");

      const res = await fetch(`${API_BASE_URL}/user/partner_referral`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          operations: [
            {
              operation: "API_INTEGRATION",
              api_integration_preference: {
                rest_api_integration: {
                  integration_method: "PAYPAL",
                  integration_type: "THIRD_PARTY",
                },
              },
            },
          ],
          products: ["PPCP", "ADVANCED_VAULTING"],
          capabilities: ["PAYPAL_WALLET_VAULTING_ADVANCED"],
          partner_config_override: {
            return_url: `${window.location.origin}/${currentPath}`,
            return_url_description: "Back to Chef Republic",
          },
        }),
      });
      const data = await res.json();

      if (data?.signup_link) {
        window.location.href = data.signup_link;
      } else {
        toast.error("PayPal onboarding failed. Please try again.");
      }
    } catch (err) {
      console.error("Onboarding error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <button
        type="button"
        className={className || "btn btn-orange rounded-pill mb-3"}
        disabled={isPaymentLinked || loading}
        onClick={handleOnboarding}
        style={{
          boxSizing: "border-box",
          maxWidth: "100%",
          width: "100%",
          backgroundColor: isPaymentLinked ? "#cccbcb" : "#FF5E41",
          color: "#fff",
          cursor: isPaymentLinked || loading ? "not-allowed" : "pointer",
          ...style,
        }}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        ) : (
          "Link Payment Method"
        )}
      </button>

      <Dialog
        open={showPaymentsReceivableError}
        onClose={() => setShowPaymentsReceivableError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment Account Issue
            </Typography>
            <IconButton
              onClick={() => setShowPaymentsReceivableError(false)}
              sx={{ color: "#666" }}
            >
              ✕
            </IconButton>
          </Box>

          <Typography variant="body1" sx={{ mb: 2, color: "#666" }}>
            {stripHtml(paymentsReceivableErrorMsg) ||
              "Your PayPal account cannot receive payments. Please review your PayPal account settings."}
          </Typography>

          <a
            href="https://www.paypal.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#4441ff", textDecoration: "underline", fontSize: "14px" }}
          >
            www.paypal.com
          </a>

          <Box sx={{ mt: 3 }}>
            <button
              onClick={() => setShowPaymentsReceivableError(false)}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              OK
            </button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={showEmailConfirmationError}
        onClose={() => setShowEmailConfirmationError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Confirmation Required
            </Typography>
            <IconButton
              onClick={() => setShowEmailConfirmationError(false)}
              sx={{ color: "#666" }}
            >
              ✕
            </IconButton>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
            {stripHtml(emailConfirmationErrorMsg) ||
              "Please confirm your PayPal email address to continue."}
          </Typography>

          <Box>
            <button
              onClick={() => setShowEmailConfirmationError(false)}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              OK
            </button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
